import { useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Calendar } from "react-date-range";
import { currentTimeValue, openNativeTimePicker } from "../utils/sessionNow";
import "./Session.css";
import "./AddSessionDialog.css";

function groupRowId(g) {
  const d = g?.groupDetails;
  if (!d) return g?.gr_id || "";
  return d.gr_id || d.dataValues?.gr_id || g?.gr_id || "";
}

function groupRowName(g) {
  const d = g?.groupDetails;
  if (!d) return g?.gr_name || "";
  return d.gr_name || d.dataValues?.gr_name || g?.gr_name || "";
}

const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--text-secondary)" },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "var(--brand-primary, #0877BC)",
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: "var(--surface-color)",
    color: "var(--text-color)",
    borderRadius: 1,
    "& fieldset": { borderColor: "var(--border-color)" },
    "&:hover fieldset": { borderColor: "var(--brand-primary, #0877BC)" },
    "&.Mui-focused fieldset": {
      borderColor: "var(--brand-primary, #0877BC)",
    },
  },
  "& input": { color: "var(--text-color)", colorScheme: "inherit" },
};

const DuplicateSessionDialog = ({
  open,
  session,
  calendarDate,
  onCalendarDateChange,
  time,
  onTimeChange,
  submitting,
  onClose,
  onConfirm,
  showGroupPicker,
  selectableGroups = [],
  selectedGroupId = "",
  onGroupChange,
  noGroupsAvailable,
  targetGroupName,
  allowPastDates = false,
}) => {
  const { t } = useTranslation();
  const timeInputRef = useRef(null);

  const applyNow = () => {
    const now = new Date();
    onCalendarDateChange(now);
    onTimeChange(currentTimeValue(now));
  };

  const sessionLabel = session?.ts_session_number
    ? t("trainingSessionNumber", {
        number: session.ts_session_number,
        defaultValue: `Training session #${session.ts_session_number}`,
      })
    : null;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      aria-labelledby="duplicate-session-title"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(var(--brand-primary-rgb), 0.35)" },
        },
      }}
    >
      <Box
        className="session-add-dialog home-theme-root"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: "440px", md: "480px" },
          maxHeight: { xs: "92vh", sm: "90vh" },
          bgcolor: "var(--surface-color)",
          color: "var(--text-color)",
          borderRadius: 2,
          border: "1px solid var(--border-color)",
          boxShadow:
            "0 12px 40px color-mix(in srgb, var(--brand-primary, #0877BC) 22%, transparent)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <Box
          sx={{
            background:
              "linear-gradient(135deg, var(--brand-primary, #0877BC) 0%, var(--brand-primary-dark, #065a94) 100%)",
            color: "#fff",
            px: { xs: 2, sm: 2.5 },
            py: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.92 }}>
            <ContentCopyIcon sx={{ fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#fff" }}>
              {t("duplicateSession") || "Duplicate"}
            </Typography>
          </Stack>
          <Typography
            id="duplicate-session-title"
            variant="h6"
            sx={{ fontWeight: 700, mt: 0.25, color: "#fff" }}
          >
            {t("duplicateSessionTitle") || "Duplicate training session"}
          </Typography>
          {sessionLabel && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9, color: "#fff" }}>
              {sessionLabel}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 2.5 },
            py: 2,
            bgcolor: "var(--bg-color, var(--surface-color))",
            color: "var(--text-color)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "var(--text-secondary)", lineHeight: 1.5 }}
          >
            {t("duplicateSessionHint") ||
              "Choose a new date and start time. The exercise plan will be copied to the new session."}
          </Typography>

          <Stack spacing={2.5}>
            {noGroupsAvailable && (
              <Alert severity="warning">
                {t("noManualGroupsForAddSession") ||
                  "No groups allow manual session creation (all may be in automatic mode)."}
              </Alert>
            )}

            {showGroupPicker && selectableGroups.length > 0 && (
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel id="duplicate-session-group-label">
                  {t("group") || "Group"}
                </InputLabel>
                <Select
                  labelId="duplicate-session-group-label"
                  label={t("group") || "Group"}
                  displayEmpty
                  value={selectedGroupId}
                  onChange={(e) => {
                    const id = e.target.value;
                    const row =
                      selectableGroups.find((g) => groupRowId(g) === id) || null;
                    onGroupChange(row);
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "var(--surface-color)",
                        color: "var(--text-color)",
                        border: "1px solid var(--border-color)",
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("selectGroup") || "Select a group"}</em>
                  </MenuItem>
                  {selectableGroups.map((g) => {
                    const gid = groupRowId(g);
                    if (!gid) return null;
                    return (
                      <MenuItem key={gid} value={gid}>
                        {groupRowName(g) || gid}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}

            {!showGroupPicker && targetGroupName && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  bgcolor: "var(--surface-color)",
                  borderRadius: 1,
                  border: "1px solid var(--border-color)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "var(--text-secondary)", fontWeight: 600 }}
                >
                  {t("group") || "Group"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mt: 0.25, color: "var(--text-color)" }}
                >
                  {targetGroupName}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1, color: "var(--text-color)" }}
              >
                {t("date") || "Date"}
              </Typography>
              <Box
                className="session-add-dialog__calendar duplicate-session-calendar"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "var(--surface-color)",
                  borderRadius: 2,
                  border: "1px solid var(--border-color)",
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 0, sm: 0.5 },
                  overflow: "hidden",
                  "& .rdrCalendarWrapper": {
                    width: "100%",
                    maxWidth: "100%",
                  },
                  "& .rdrMonth": {
                    width: "100%",
                  },
                }}
              >
                <Calendar
                  date={calendarDate}
                  onChange={onCalendarDateChange}
                  showMonthAndYearPickers={allowPastDates}
                />
              </Box>
            </Box>

            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label={t("starttime") || "Start time"}
                type="time"
                value={time}
                onChange={(e) => onTimeChange(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                inputRef={timeInputRef}
                inputProps={{
                  step: 300,
                  className: "asd__input--time",
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      edge="end"
                      onClick={() => openNativeTimePicker(timeInputRef.current)}
                      aria-label={t("openTimePicker") || "Open time picker"}
                      sx={{ color: "var(--text-color)" }}
                    >
                      <AccessTimeIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={fieldSx}
              />
              <Button
                variant="outlined"
                onClick={applyNow}
                sx={{
                  mb: "1px",
                  minWidth: 72,
                  height: 56,
                  borderColor: "var(--brand-primary, #0877BC)",
                  color: "var(--brand-primary, #0877BC)",
                }}
              >
                {t("now") || "Now"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            borderTop: "1px solid var(--border-color)",
            bgcolor: "var(--surface-color)",
          }}
        >
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.5}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
              fullWidth
              sx={{
                borderColor: "var(--brand-primary, #0877BC)",
                color: "var(--brand-primary, #0877BC)",
              }}
            >
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={submitting || noGroupsAvailable}
              fullWidth
              sx={{
                bgcolor: "var(--brand-primary, #0877BC)",
                color: "#fff",
                "&:hover": { bgcolor: "var(--brand-primary-dark, #065a94)" },
              }}
            >
              {submitting
                ? t("loading") || "Loading…"
                : t("duplicateSession") || "Duplicate"}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default DuplicateSessionDialog;
